import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import debounce from 'lodash/debounce';
import { AnnouncerV2 } from '@atlassian/jira-accessibility/src/ui/announcer-v2/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { DEBOUNCE_DELAY } from '../../../../../../common/constants.tsx';
import messages from './messages.tsx';

export type Props = {
	query: string;
	resultsCount: number;
	isAsyncSearch: boolean;
	isLoading: boolean;
};

/** Generate a JSX friendly string filled with spaces */
const generateRandomSpaces = (max = 100) => {
	const numSpaces = Math.round(Math.random() * max);
	return new Array(numSpaces).fill('\u00a0').join('');
};

const appendRandomSpacesToString = (text: string) => `${text}${generateRandomSpaces()}`;

/**
 *
 * Triggers screenreader announcements of loading state and search result count
 */
const ResultsAnnouncer = ({ query, resultsCount, isAsyncSearch, isLoading }: Props) => {
	const [message, setMessage] = useState('');
	const hasMounted = useRef(false);
	const prevLoading = useRef(false);
	const prevQuery = useRef(query);
	const prevCount = useRef(resultsCount);
	const { formatMessage } = useIntl();

	const setResultsMessage = useCallback(
		(count: number) => {
			setMessage(
				/**
				 * We need to append a random number of empty spaces to messages to
				 * make them unique and force a re-announcement for two reasons:
				 * 1) When the query has changed but the results count is the same,
				 *    so the message wouldn't have changed
				 * 2) During VoiceOver testing, it appeared that on ocassion the same
				 *    message being output in a short space of time; even when a
				 *    unique message was sandwiched in between; lead to messages being
				 *    ignored by the screenreader.
				 *
				 * Although in theory AnnouncerV2 is built to handle these scenarios,
				 * in testing this was not the case.
				 */
				appendRandomSpacesToString(formatMessage(messages.resultsAnnouncement, { count })),
			);
		},
		[formatMessage],
	);

	/**
	 * The updating of the message is debounced as 'query'
	 * updates in real time, but the on change callback
	 * that triggers the updating of results is itself debounced.
	 * We only want to update the message once we've received the
	 * new results, not everytime the query changes.
	 */
	const setMessageDebounced = useMemo(
		() =>
			debounce((newCount: number, newQuery: string, loading: boolean) => {
				if (loading) {
					setMessage(appendRandomSpacesToString(formatMessage(messages.loading)));
				}
				// If using async search, we want to make sure we're only
				// announcing fresh results, not re-announcing stale ones
				else if (isAsyncSearch && prevLoading.current) {
					setResultsMessage(newCount);
				} else if (!isAsyncSearch && newQuery !== prevQuery.current) {
					setResultsMessage(newCount);
				}
				prevLoading.current = loading;
				prevCount.current = newCount;
				prevQuery.current = newQuery;
			}, DEBOUNCE_DELAY + 50),
		[isAsyncSearch, formatMessage, setResultsMessage],
	);

	useEffect(() => {
		if (!hasMounted.current) {
			hasMounted.current = true;
		} else {
			setMessageDebounced(resultsCount, query, isLoading);
		}
	}, [isLoading, query, resultsCount, setMessageDebounced]);

	return <AnnouncerV2 message={message} />;
};

export default ResultsAnnouncer;
